/**
 * Module de chargement dynamique du contenu des onglets
 */
import contextStore from '../common/stores/contextStore.js';
import { logger } from '../utils/debug/logger.js';

// Cache pour stocker les templates déjà chargés
const templateCache = new Map();

// Map des noms de fichiers de template pour chaque onglet
const TAB_TEMPLATES = {
  tab1: 'fan.ejs',
  tab2: 'map.ejs',
  tab3: 'tree.ejs',
  tab4: 'timeline.ejs',
  tab5: 'statistics.ejs',
  tab6: 'tools.ejs',
  tab7: 'photos.ejs',
  tab8: 'fusion.ejs'
};

/**
 * Charge le contenu HTML d'un onglet
 * @param {string} tabId - L'ID de l'onglet (ex: 'tab1')
 * @returns {Promise<string>} Le contenu HTML de l'onglet
 */
export async function loadTabContent(tabId) {
  logger.tabs.content.group(`📄 Chargement du contenu pour ${tabId}`);
  
  try {
    // Vérifier le cache
    if (templateCache.has(tabId)) {
      logger.tabs.content.debug('✓ Contenu trouvé dans le cache');
      logger.tabs.content.groupEnd();
      return templateCache.get(tabId);
    }
    
    // Déterminer le nom du fichier template
    const templateFile = TAB_TEMPLATES[tabId];
    if (!templateFile) {
      throw new Error(`Aucun template défini pour l'onglet ${tabId}`);
    }
    
    // Construire l'URL du template selon l'environnement pour éviter l'injection en mode proxy
    const templateUrl = contextStore.isProxied 
      ? `/app/assets/html/tabs/${templateFile}` // Mode proxy : chemin absolu requis
      : `assets/html/tabs/${templateFile}`;     // Mode direct/dev : chemin relatif
    logger.tabs.content.debug(`🔗 Chargement depuis: ${templateUrl}`);
    
    // Charger le template
    const response = await fetch(templateUrl);
    if (!response.ok) {
      throw new Error(`Erreur lors du chargement du template: ${response.status}`);
    }
    
    const content = await response.text();
    
    // Mettre en cache
    templateCache.set(tabId, content);
    logger.tabs.content.debug('✓ Contenu chargé et mis en cache');
    
    logger.tabs.content.groupEnd();
    return content;
    
  } catch (error) {
    logger.tabs.content.error(`❌ Erreur lors du chargement du contenu pour ${tabId}:`, error);
    logger.tabs.content.groupEnd();
    throw error;
  }
}

/**
 * Injecte le contenu dans l'onglet et supprime le placeholder
 * @param {string} tabId - L'ID de l'onglet
 * @param {string} content - Le contenu HTML à injecter
 */
export function injectTabContent(tabId, content) {
  logger.tabs.content.group(`💉 Injection du contenu dans ${tabId}`);
  
  try {
    const tabElement = document.getElementById(tabId);
    if (!tabElement) {
      throw new Error(`Élément ${tabId} non trouvé`);
    }
    
    // Supprimer le placeholder de chargement
    const placeholder = tabElement.querySelector('.tab-loading-placeholder');
    if (placeholder) {
      placeholder.remove();
      logger.tabs.content.debug('✓ Placeholder de chargement supprimé');
    }
    
    // Injecter le nouveau contenu
    tabElement.innerHTML = content;
    logger.tabs.content.debug('✓ Contenu injecté avec succès');
    
    logger.tabs.content.groupEnd();
    return true;
    
  } catch (error) {
    logger.tabs.content.error(`❌ Erreur lors de l'injection du contenu:`, error);
    logger.tabs.content.groupEnd();
    return false;
  }
}

/**
 * Précharge un ou plusieurs templates en arrière-plan
 * @param {string|string[]} tabIds - L'ID ou les IDs des onglets à précharger
 */
export async function preloadTabContent(tabIds) {
  const idsToPreload = Array.isArray(tabIds) ? tabIds : [tabIds];
  
  logger.tabs.content.debug(`🔄 Préchargement des templates:`, idsToPreload);
  
  const promises = idsToPreload.map(tabId => 
    loadTabContent(tabId).catch(error => {
      logger.tabs.content.warn(`⚠️ Échec du préchargement pour ${tabId}:`, error);
    })
  );
  
  await Promise.all(promises);
}

/**
 * Vide le cache des templates
 */
export function clearTemplateCache() {
  templateCache.clear();
  logger.tabs.content.debug('🗑️ Cache des templates vidé');
}